import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useGenerateGroceryList } from '@/features/grocery-list/useGenerateGroceryList'
import { useCompleteOnboarding } from './useCompleteOnboarding'

interface Props {
  mealsAddedCount: number
  slotsFilled: number
  onRecordCompletion?: (metrics: { mealsAdded: number; slotsFilled: number }) => void
}

export function OnboardingGenerateStep({ mealsAddedCount, slotsFilled, onRecordCompletion }: Props) {
  const { t } = useTranslation('onboarding')
  const navigate = useNavigate()
  const generateMutation = useGenerateGroceryList()
  const completeMutation = useCompleteOnboarding()
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current) return
    fired.current = true

    generateMutation.mutate(undefined, {
      onSuccess: () => {
        onRecordCompletion?.({ mealsAdded: mealsAddedCount, slotsFilled })
        completeMutation.mutate(undefined, {
          onSuccess: () => {
            sessionStorage.removeItem('onboarding_meals_added')
            navigate({ to: '/home' })
          },
        })
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasError = generateMutation.isError || completeMutation.isError

  const handleRetry = () => {
    if (completeMutation.isError && !generateMutation.isError) {
      // Only complete failed — skip re-generation
      completeMutation.reset()
      completeMutation.mutate(undefined, {
        onSuccess: () => {
          sessionStorage.removeItem('onboarding_meals_added')
          navigate({ to: '/home' })
        },
      })
      return
    }
    // Generate failed (or both) — redo full sequence
    generateMutation.reset()
    completeMutation.reset()
    generateMutation.mutate(undefined, {
      onSuccess: () => {
        onRecordCompletion?.({ mealsAdded: mealsAddedCount, slotsFilled })
        completeMutation.mutate(undefined, {
          onSuccess: () => {
            sessionStorage.removeItem('onboarding_meals_added')
            navigate({ to: '/home' })
          },
        })
      },
    })
  }

  return (
    <div className="flex flex-col flex-1 items-center justify-center">
      <h1 className="text-xl font-bold text-ink mb-2">{t('generate.heading')}</h1>
      {(generateMutation.isPending || completeMutation.isPending) && (
        <div className="flex flex-col items-center gap-4">
          <div role="status" aria-label={t('generate.loading')} className="h-12 w-12 animate-spin rounded-full border-4 border-line border-t-accent" />
          <p className="text-muted text-sm">{t('generate.loading')}</p>
        </div>
      )}
      {hasError && (
        <div className="flex flex-col items-center gap-4">
          <p className="text-red-600 font-semibold">{t('generate.error')}</p>
          <button
            type="button"
            disabled={generateMutation.isPending || completeMutation.isPending}
            onClick={handleRetry}
            className="rounded-lg bg-accent text-white font-semibold py-2 px-6 hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('generate.retry')}
          </button>
        </div>
      )}
      {completeMutation.isSuccess && (
        <p className="text-accent font-semibold">{t('generate.success')}</p>
      )}
    </div>
  )
}
